import {common, priority} from '@priority-software/priority-proto-api/lib';
import {getOrigValue} from '../../stores/Form/utils';
import {
    FAMILY_DESC_NAME,
    FAMILY_EDESC_NAME,
    FAMILY_FIELD_NAME,
    NAME_TO_KEY_MAP,
    PART_INVENTORY_FIELDS,
    PART_PARENT_DEFAULT_FIELDS,
    UNIT_FIELD_NAME
} from './consts';
import {ColumnsData, FamilyResults, PartsDetails, PartsSearchResults} from './types';
import {createObjectURL} from '../../features/Form/utils/file-processing';

export const serializeFamilyOptions = (
    records: priority.netitems.form.IRecord[],
    columns: priority.netitems.form.IColumn[]
): FamilyResults[] => {
    if (!records?.length && !columns?.length) return [];
    const columnMap = columns.reduce((map, col) => {
        map[col.Name] = col;
        return map;
    }, {});

    return records.map(record => {
        const getFieldValue = fieldName => {
            const column = columnMap[fieldName];
            const field = record.Fields.find(fld => fld.ColumnID === column?.ID);
            return getOrigValue(field?.Value, column);
        };

        const fieldNameVal = getFieldValue(FAMILY_FIELD_NAME);

        return {
            retval: fieldNameVal,
            string1: fieldNameVal,
            string2: getFieldValue(FAMILY_EDESC_NAME),
            string3: getFieldValue(FAMILY_DESC_NAME)
        };
    });
};

export const serializeSearchResults = (
    searchResults: priority.netitems.form.FieldSelectRs
): PartsSearchResults => {
    return {
        partsNames: searchResults?.Values?.map(part => part.Value) || [],
        hasMore: !!searchResults.HaveMore
    };
};

export const serializeInventoryDetails = (
    record: priority.netitems.form.IField[] = [],
    columns: priority.netitems.form.IColumn[],
    selectedColumns: string[]
): ColumnsData[] => {
    const unitField = columns.find(col => col.Name === UNIT_FIELD_NAME);
    const relevantColumns = selectedColumns.reduce((acc, value) => {
        if (PART_INVENTORY_FIELDS.includes(value)) {
            const column = columns.find(col => col.Name === value);
            column && acc.set(column.ID, column);
        }
        return acc;
    }, new Map<number | Long, priority.netitems.form.IColumn>([[unitField.ID, unitField]]));

    const defaultData: ColumnsData[] = Array.from(relevantColumns.values()).map(col => ({
        columnName: col.Name,
        columnTitle: col.Title,
        value: col.Name === UNIT_FIELD_NAME ? '' : 0,
        columnId: 0
    }));

    //create InventoryData with the relevant fields from records fields
    const res = record?.reduce((acc, value) => {
        const column = relevantColumns.get(value.ColumnID);
        if (column) {
            const colTooUpdate = acc.findIndex(col => col.columnName === column.Name);
            acc[colTooUpdate] = {
                columnTitle: column.Title,
                columnName: column.Name,
                columnId: column.ID,
                value: getOrigValue(value.Value, column) || acc[colTooUpdate].value
            };
        }
        return acc;
    }, defaultData);

    return res;
};

const getAttachmentValue = (value: common.IConstant) => {
    if (value?.File?.Data) {
        return createObjectURL(value);
    }
    return null;
};

export const serializePartsDetails = (
    records: priority.netitems.form.IRecord[],
    columns: priority.netitems.form.IColumn[],
    selectedItems: Map<string, PartsDetails>
): PartsDetails[] => {
    //get relevant columns ids
    const relevantColumns = columns.reduce(
        (acc, value) => {
            if (PART_PARENT_DEFAULT_FIELDS.includes(value.Name) || value.Key) {
                acc.defaultColumns.set(value.ID, value);
            }
            return acc;
        },
        {
            defaultColumns: new Map<number | Long, priority.netitems.form.IColumn>(),
            selectedColumns: new Map<number | Long, priority.netitems.form.IColumn>()
        }
    );

    const parts = records.map((record, index) => {
        const partDetails: PartsDetails = {
            partName: '',
            description: '',
            ePartdescription: '',
            familyName: '',
            family: '',
            columns: [],
            keys: [],
            selectedQuant: 0
        };

        record.Fields.forEach(value => {
            const defaultColumn = relevantColumns.defaultColumns.get(value.ColumnID);
            if (defaultColumn) {
                const key = NAME_TO_KEY_MAP[defaultColumn.Name];
                if (key) {
                    partDetails[key] = getOrigValue(value.Value, defaultColumn);
                }
                if (defaultColumn.Key) {
                    partDetails.keys.push(value);
                }
            }
            const column = columns.find(col => col.ID === value.ColumnID);
            if (column) {
                partDetails.columns.push({
                    columnName: column.Name,
                    columnTitle: column.Title,
                    columnId: column.ID,
                    value: getPartsValue(column, value)
                });
            }
        });

        const selectedItem = selectedItems.get(partDetails.partName);
        if (selectedItem) {
            partDetails.selectedQuant = selectedItem.selectedQuant;
        }

        return partDetails;
    });
    return parts;
};

const getPartsValue = (column, value) => {
    if (column.Type === priority.netitems.form.ColumnType.Attachment) {
        return getAttachmentValue(value.Value);
    } else if (column.Type === priority.netitems.form.ColumnType.Boolean) {
        return getOrigValue(value.Value, column) ? '🗹' : '☐';
    }

    return getOrigValue(value.Value, column) || '-';
};
